export interface SupplierRoiResult {
  annualSavings: number;
  addedRevenue: number;
  totalBenefit: number;
  netYear1: number;
  roiPct: number;
}

type NumberMap = Record<string, number>;

export interface RoiInputs {
  metrics: NumberMap;
  supplierProposals: Record<string, NumberMap>;
}

const WORK_MINUTES_PER_YEAR = 2080 * 60;
const DEFAULT_FTE_SALARY = 50000;
const UNCAPPED_ROI_SENTINEL = 999;

const costPerMinute = (metrics: NumberMap) => (metrics.agent_salary || 0) / WORK_MINUTES_PER_YEAR;

const annualLaborSavings = (metrics: NumberMap, proposal: NumberMap): number => {
  const currentAht = metrics.avg_handle_time || 0;
  const cpm = costPerMinute(metrics);

  const totalCalls = (metrics.annual_inbound_calls || 0) + (metrics.annual_outbound_calls || 0);
  const ahtSavings = totalCalls * currentAht * ((proposal.aht_reduction_pct || 0) / 100) * cpm;

  const deflectedCalls = (metrics.annual_inbound_calls || 0) * ((proposal.deflection_rate_pct || 0) / 100);
  const deflectionSavings = deflectedCalls * currentAht * cpm;

  const fteCount = (metrics.it_fte || 0) + (metrics.qa_fte || 0) + (metrics.wfm_fte || 0);
  const fteSavings =
    fteCount * ((proposal.fte_reduction_pct || 0) / 100) * (metrics.agent_salary || DEFAULT_FTE_SALARY);

  return ahtSavings + deflectionSavings + fteSavings;
};

const annualAddedRevenue = (metrics: NumberMap, proposal: NumberMap): number =>
  (((metrics.annual_outbound_calls || 0) * (metrics.conversion_rate || 0)) / 100) *
  ((proposal.conversion_improvement_pct || 0) / 100) *
  (metrics.revenue_per_opportunity || 0);

export const calcSupplierRoi = (
  currentMonthlySpend: number,
  metrics: NumberMap,
  proposal: NumberMap
): SupplierRoiResult => {
  const currentAnnual = currentMonthlySpend * 12;
  const proposedAnnual = (proposal.proposed_monthly_cost || 0) * 12;
  const implCost = proposal.implementation_cost || 0;

  const licensingSavings = currentAnnual - proposedAnnual;
  const annualSavings = licensingSavings + annualLaborSavings(metrics, proposal);
  const addedRevenue = annualAddedRevenue(metrics, proposal);
  const totalBenefit = annualSavings + addedRevenue;
  const netYear1 = totalBenefit - implCost;
  const roiPct = implCost > 0 ? (netYear1 / implCost) * 100 : totalBenefit > 0 ? UNCAPPED_ROI_SENTINEL : 0;

  return { annualSavings, addedRevenue, totalBenefit, netYear1, roiPct };
};
